import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ChatArea from './components/ChatArea';
import InputArea from './components/InputArea';
import { sendMessageToChatGPT, formatMessagesForAPI } from './services/chatService';
import './App.css';

function App() {
  // بارگذاری مکالمات از localStorage
  const [conversations, setConversations] = useState(() => {
    const savedConversations = localStorage.getItem('conversations');
    return savedConversations ? JSON.parse(savedConversations) : [];
  });

  const [activeConversation, setActiveConversation] = useState(null);
  
  // پیام‌های مکالمه فعلی
  const [messages, setMessages] = useState([]);

  const [isTyping, setIsTyping] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const inputRef = useRef();

  // ذخیره مکالمات در localStorage هنگام تغییر
  useEffect(() => {
    localStorage.setItem('conversations', JSON.stringify(conversations));
  }, [conversations]);

  // بارگذاری پیام‌های مکالمه فعال
  useEffect(() => {
    if (activeConversation) {
      const conversation = conversations.find(c => c.id === activeConversation);
      if (conversation) {
        setMessages(conversation.messages || []);
      }
    } else {
      setMessages([]);
    }
  }, [activeConversation, conversations]);

  const handleSendMessage = async (content) => {
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content,
      timestamp: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
    };

    let currentConversationId = activeConversation;

    // اگر مکالمه فعالی نیست، یک مکالمه جدید بساز
    if (!currentConversationId) {
      const title = content.substring(0, 30) + (content.length > 30 ? '...' : '');
      const newConversationId = Date.now();
      const newConversation = {
        id: newConversationId,
        title,
        time: 'همین الان',
        messages: [userMessage]
      };
      
      currentConversationId = newConversationId;
      setConversations(prev => [newConversation, ...prev]);
      setActiveConversation(newConversationId);
      setMessages([userMessage]);
    } else {
      // به‌روزرسانی مکالمه فعلی
      setMessages((prev) => [...prev, userMessage]);
      setConversations(prev => prev.map(conv => 
        conv.id === currentConversationId 
          ? { ...conv, messages: [...(conv.messages || []), userMessage], time: 'همین الان' }
          : conv
      ));
    }

    setIsTyping(true);

    // استفاده از ChatGPT API واقعی از طریق PHP
    try {
      const response = await sendMessageToChatGPT(content, formatMessagesForAPI(messages));
      const botMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response,
        timestamp: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      
      // به‌روزرسانی مکالمه با پاسخ ربات - استفاده از currentConversationId
      setConversations(prev => prev.map(conv => 
        conv.id === currentConversationId 
          ? { ...conv, messages: [...(conv.messages || []), botMessage] }
          : conv
      ));
    } catch (error) {
      console.error('ChatGPT Error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'متأسفانه در ارتباط با سرور مشکلی پیش آمد. لطفاً دوباره تلاش کنید.\n\nخطا: ' + error.message,
        timestamp: new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, errorMessage]);
      
      setConversations(prev => prev.map(conv => 
        conv.id === currentConversationId 
          ? { ...conv, messages: [...(conv.messages || []), errorMessage] }
          : conv
      ));
    } finally {
      setIsTyping(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setActiveConversation(null);
  };

  const handleSelectConversation = (id) => {
    setActiveConversation(id);
  };

  const handleDeleteConversation = (id) => {
    setConversations(prev => prev.filter(conv => conv.id !== id));
    if (activeConversation === id) {
      setActiveConversation(null);
      setMessages([]);
    }
  };

  // هندلر برای کلیک روی سوالات سریع
  const handleQuickQuestionClick = (questionTemplate) => {
    if (inputRef.current) {
      inputRef.current.setInputText(questionTemplate);
    }
  };

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        
        <div className="flex pt-14 sm:pt-16 h-screen">
          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col w-full">
            <ChatArea 
              messages={messages} 
              isTyping={isTyping}
              onQuickQuestionClick={handleQuickQuestionClick}
            />
            <InputArea 
              ref={inputRef}
              onSendMessage={handleSendMessage} 
              disabled={isTyping} 
            />
          </div>

          {/* Sidebar */}
          <Sidebar
            conversations={conversations}
            activeConversation={activeConversation}
            onNewChat={handleNewChat}
            onSelectConversation={handleSelectConversation}
            onDeleteConversation={handleDeleteConversation}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        </div>
      </div>
    </ThemeProvider>
  );
}

export default App;
